import React, { createContext, useContext, useEffect, useState } from "react";
import http from "../api/apiClient";
import { AuthContext } from "../AuthProvider";
import { useCan } from "../authz/useCan";
import Spinner from "../components/Spinner";

export interface FeatureFlags {
  schoolTasks: boolean;
  /** School tasks is switched on and the current school year is open for parents. */
  schoolTasksForParents: boolean;
}

interface FeatureFlagsState {
  flags: FeatureFlags;
  /** False until the backend answered or failed, so routes do not decide too early. */
  loaded: boolean;
}

const SWITCHED_OFF: FeatureFlags = { schoolTasks: false, schoolTasksForParents: false };

const FeatureFlagsContext = createContext<FeatureFlagsState>({ flags: SWITCHED_OFF, loaded: false });

/**
 * Reads once after login which optional features the backend has switched on (GET /features).
 * Anything that cannot be read counts as switched off.
 */
export function FeatureFlagsProvider({ children }: { children: React.ReactNode }) {
  const { authenticated } = useContext(AuthContext) as { authenticated: boolean };
  const [state, setState] = useState<FeatureFlagsState>({ flags: SWITCHED_OFF, loaded: false });

  useEffect(() => {
    if (!authenticated) {
      setState({ flags: SWITCHED_OFF, loaded: true });
      return undefined;
    }
    let active = true;
    setState({ flags: SWITCHED_OFF, loaded: false });
    http.get("/features")
      .then(({ data }) => {
        if (active) setState({ flags: { schoolTasks: data?.schoolTasks === true, schoolTasksForParents: data?.schoolTasksForParents === true }, loaded: true });
      })
      .catch(() => {
        if (active) setState({ flags: SWITCHED_OFF, loaded: true });
      });
    return () => {
      active = false;
    };
  }, [authenticated]);

  return <FeatureFlagsContext.Provider value={state}>{children}</FeatureFlagsContext.Provider>;
}

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

/**
 * Where School tasks may show up: only while the backend has it switched on, and then as the
 * permissions allow. Admin menu: any School tasks permission; parent portal: viewing tasks, and only
 * once the current school year is open for parents.
 */
export function useSchoolTasksVisibility() {
  const { flags } = useFeatureFlags();
  const viewTasks = useCan("school.tasks", "view");
  const manageTasks = useCan("school.tasks", "manage");
  const viewSignups = useCan("school.task-signups", "view");
  const manageSignups = useCan("school.task-signups", "manage");
  const viewCleaning = useCan("school.cleaning", "view");
  const manageCleaning = useCan("school.cleaning", "manage");
  return {
    admin: flags.schoolTasks && (viewTasks || manageTasks || viewSignups || manageSignups || viewCleaning || manageCleaning),
    parentPortal: flags.schoolTasksForParents && viewTasks,
  };
}

/** Renders its children only while School tasks is switched on; otherwise the fallback, such as "not found". */
export function RequireSchoolTasks({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const { flags, loaded } = useFeatureFlags();
  if (!loaded) return <Spinner />;
  return <>{flags.schoolTasks ? children : fallback}</>;
}
